import imageCompression from 'browser-image-compression';

export interface CompressionProfile {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  quality: number;
  useWebWorker: boolean;
  fileType?: string;
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  dimensions: { width: number; height: number };
}

export const COMPRESSION_PROFILES = {
  HERO_CAROUSEL: {
    maxSizeMB: 0.4,
    maxWidthOrHeight: 1920,
    quality: 0.85,
    useWebWorker: true,
    fileType: 'image/webp'
  },
  STORY_PROFILE: {
    maxSizeMB: 0.2,
    maxWidthOrHeight: 800,
    quality: 0.80,
    useWebWorker: true,
    fileType: 'image/webp'
  },
  QUESTION_IMAGE: {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 1200,
    quality: 0.82,
    useWebWorker: true,
    fileType: 'image/webp'
  },
  MEMORY_IMAGE: {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 1200,
    quality: 0.82,
    useWebWorker: true,
    fileType: 'image/webp'
  }
} as const;

export const supportsWebP = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
};

export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
};

export const validateImageDimensions = (
  width: number,
  height: number,
  minWidth = 100,
  minHeight = 100,
  maxWidth = 8000,
  maxHeight = 8000
): { isValid: boolean; error?: string } => {
  if (width < minWidth || height < minHeight) {
    return {
      isValid: false,
      error: `La imagen es demasiado pequeña. Mínimo ${minWidth}x${minHeight}px`
    };
  }

  if (width > maxWidth || height > maxHeight) {
    return {
      isValid: false,
      error: `La imagen es demasiado grande. Máximo ${maxWidth}x${maxHeight}px`
    };
  }

  return { isValid: true };
};

export const compressImage = async (
  file: File,
  profile: CompressionProfile,
  onProgress?: (progress: number) => void
): Promise<CompressionResult> => {
  const originalSize = file.size;

  try {
    const dimensions = await getImageDimensions(file);

    const options = {
      maxSizeMB: profile.maxSizeMB,
      maxWidthOrHeight: profile.maxWidthOrHeight,
      useWebWorker: profile.useWebWorker,
      fileType: supportsWebP() ? profile.fileType : 'image/jpeg',
      initialQuality: profile.quality,
      onProgress: onProgress
    };

    const compressedFile = await imageCompression(file, options);

    const compressionRatio = originalSize > 0
      ? ((originalSize - compressedFile.size) / originalSize) * 100
      : 0;

    return {
      file: compressedFile,
      originalSize,
      compressedSize: compressedFile.size,
      compressionRatio,
      dimensions
    };
  } catch (error) {
    console.error('Error compressing image:', error);
    throw new Error('Failed to compress image');
  }
};

export const getCompressionStats = (result: CompressionResult): string => {
  const originalSizeMB = (result.originalSize / (1024 * 1024)).toFixed(2);
  const compressedSizeMB = (result.compressedSize / (1024 * 1024)).toFixed(2);
  const ratio = result.compressionRatio.toFixed(1);

  return `Original: ${originalSizeMB}MB → Comprimido: ${compressedSizeMB}MB (${ratio}% reducción)`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const compressImagesBatch = async (
  files: File[],
  profile: CompressionProfile,
  onProgress?: (fileIndex: number, progress: number) => void
): Promise<CompressionResult[]> => {
  const results: CompressionResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const result = await compressImage(
      files[i],
      profile,
      (progress) => onProgress?.(i, progress)
    );
    results.push(result);
  }

  return results;
};
